import { useCallback, useEffect, useState } from 'react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { DatePicker } from '@/components/ui/DatePicker';
import { FormGrid } from '@/components/ui/FormGrid';
import { Select } from '@/components/ui/Select';
import { TablePagination } from '@/components/ui/TablePagination';
import { usePagination } from '@/hooks/usePagination';
import { usePermissions } from '@/hooks/usePermissions';
import { financeApi } from '@/api/finance.api';
import { showApiError } from '@/utils/apiError';
import type { FinAccount, FinJournalEntry, FinPendingApproval } from '@/types/finance';
import { notify } from '@/core/toast/notify';

const ACCOUNT_TYPES = ['ASSET', 'LIABILITY', 'EQUITY', 'INCOME', 'EXPENSE'];

export function FinanceAccountingPage() {
  const { hasAny } = usePermissions();
  const canManage = hasAny('FINANCE_MANAGE', 'FINANCE_JOURNAL_MANAGE');
  const accountsPagination = usePagination(20);
  const journalsPagination = usePagination(15);
  const [accounts, setAccounts] = useState<FinAccount[]>([]);
  const [accountOptions, setAccountOptions] = useState<FinAccount[]>([]);
  const [journals, setJournals] = useState<FinJournalEntry[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [journalsLoading, setJournalsLoading] = useState(false);
  const [pending, setPending] = useState<FinPendingApproval[]>([]);
  const [showCoaForm, setShowCoaForm] = useState(false);
  const [showJournalForm, setShowJournalForm] = useState(false);
  const [coaForm, setCoaForm] = useState({ accountCode: '', accountName: '', accountType: 'EXPENSE' });
  const [journalForm, setJournalForm] = useState({
    referenceType: 'MANUAL',
    transactionDate: new Date().toISOString().slice(0, 10),
    description: '',
    debitAccountId: 0,
    creditAccountId: 0,
    amount: '',
  });

  const loadAccounts = useCallback(async () => {
    setAccountsLoading(true);
    try {
      const acc = await financeApi.listAccounts({
        page: accountsPagination.page,
        size: accountsPagination.size,
      });
      setAccounts(acc.content);
      accountsPagination.applyPageResponse(acc);
    } catch (e) {
      showApiError(e, 'Failed to load accounts');
    } finally {
      setAccountsLoading(false);
    }
  }, [accountsPagination.page, accountsPagination.size, accountsPagination]);

  const loadJournals = useCallback(async () => {
    setJournalsLoading(true);
    try {
      const jnl = await financeApi.listJournals({
        page: journalsPagination.page,
        size: journalsPagination.size,
      });
      setJournals(jnl.content);
      journalsPagination.applyPageResponse(jnl);
    } catch (e) {
      showApiError(e, 'Failed to load journals');
    } finally {
      setJournalsLoading(false);
    }
  }, [journalsPagination.page, journalsPagination.size, journalsPagination]);

  const loadPending = useCallback(() => {
    financeApi
      .listPendingApprovals()
      .then(setPending)
      .catch(() => setPending([]));
  }, []);

  const loadAccountOptions = useCallback(() => {
    financeApi
      .listAccounts({ page: 0, size: 500 })
      .then((r) => setAccountOptions(r.content))
      .catch(() => setAccountOptions([]));
  }, []);

  const reloadAll = useCallback(() => {
    void loadAccounts();
    void loadJournals();
    loadPending();
    loadAccountOptions();
  }, [loadAccounts, loadJournals, loadPending, loadAccountOptions]);

  useEffect(() => {
    void loadAccounts();
  }, [loadAccounts]);

  useEffect(() => {
    void loadJournals();
  }, [loadJournals]);

  useEffect(() => {
    loadPending();
    loadAccountOptions();
  }, [loadPending, loadAccountOptions]);

  const approve = async (id: number) => {
    try {
      await financeApi.approveJournal(id);
      notify.success('Journal approved');
      reloadAll();
    } catch (e) {
      showApiError(e, 'Approve failed');
    }
  };

  const post = async (id: number) => {
    try {
      await financeApi.postApprovedJournal(id);
      notify.success('Journal posted');
      reloadAll();
    } catch (e) {
      showApiError(e, 'Post failed');
    }
  };

  const approvePending = async (item: FinPendingApproval) => {
    if (item.entityType !== 'JOURNAL_ENTRY' || item.entityId == null) {
      notify.error('Only journal entries can be approved from this queue');
      return;
    }
    await approve(item.entityId);
  };

  const createAccount = async () => {
    if (!coaForm.accountCode.trim() || !coaForm.accountName.trim()) {
      notify.error('Account code and name are required');
      return;
    }
    try {
      await financeApi.createAccount(coaForm);
      notify.success('Account created');
      setShowCoaForm(false);
      setCoaForm({ accountCode: '', accountName: '', accountType: 'EXPENSE' });
      reloadAll();
    } catch (e) {
      showApiError(e, 'Create account failed');
    }
  };

  const createJournalDraft = async () => {
    const amount = Number(journalForm.amount);
    if (!amount || amount <= 0 || !journalForm.debitAccountId || !journalForm.creditAccountId) {
      notify.error('Select accounts and enter a positive amount');
      return;
    }
    try {
      await financeApi.createDraftJournal({
        referenceType: journalForm.referenceType,
        transactionDate: journalForm.transactionDate,
        description: journalForm.description || undefined,
        lines: [
          { accountId: journalForm.debitAccountId, debitAmount: amount },
          { accountId: journalForm.creditAccountId, creditAmount: amount },
        ],
      });
      notify.success('Journal draft created');
      setShowJournalForm(false);
      reloadAll();
    } catch (e) {
      showApiError(e, 'Create journal failed');
    }
  };

  const selectAccountOptions = accountOptions.map((a) => ({
    value: String(a.id),
    label: `${a.accountCode} — ${a.accountName}`,
  }));

  return (
    <div>
      <PageHeader
        title="Accounting"
        description="Chart of accounts, journal composer, multi-stage approval, draft → approve → post"
        breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'Finance', to: '/finance' }, { label: 'Accounting' }]}
        actions={
          canManage ? (
            <div className="flex gap-2">
              <Button variant="secondary" onClick={() => setShowCoaForm((v) => !v)}>
                {showCoaForm ? 'Cancel account' : 'New account'}
              </Button>
              <Button onClick={() => setShowJournalForm((v) => !v)}>
                {showJournalForm ? 'Cancel journal' : 'New journal'}
              </Button>
            </div>
          ) : undefined
        }
      />

      {canManage && showCoaForm && (
        <div className="card p-4 mb-4 grid gap-3 md:grid-cols-4">
          <Input
            placeholder="Account code"
            value={coaForm.accountCode}
            onChange={(e) => setCoaForm({ ...coaForm, accountCode: e.target.value.toUpperCase() })}
          />
          <Input
            placeholder="Account name"
            value={coaForm.accountName}
            onChange={(e) => setCoaForm({ ...coaForm, accountName: e.target.value })}
          />
          <Select
            value={coaForm.accountType}
            onChange={(v) => setCoaForm({ ...coaForm, accountType: v })}
            options={ACCOUNT_TYPES.map((t) => ({ value: t, label: t }))}
          />
          <Button onClick={() => void createAccount()}>Save account</Button>
        </div>
      )}

      {canManage && showJournalForm && (
        <FormGrid columns={3} className="card p-4 mb-4">
          <DatePicker
            value={journalForm.transactionDate}
            onChange={(v) => setJournalForm({ ...journalForm, transactionDate: v })}
          />
          <Input
            placeholder="Description"
            value={journalForm.description}
            onChange={(e) => setJournalForm({ ...journalForm, description: e.target.value })}
          />
          <Input
            type="number"
            placeholder="Amount"
            value={journalForm.amount}
            onChange={(e) => setJournalForm({ ...journalForm, amount: e.target.value })}
          />
          <Select
            value={String(journalForm.debitAccountId || '')}
            onChange={(v) => setJournalForm({ ...journalForm, debitAccountId: Number(v) })}
            options={[{ value: '', label: 'Debit account…' }, ...selectAccountOptions]}
          />
          <Select
            value={String(journalForm.creditAccountId || '')}
            onChange={(v) => setJournalForm({ ...journalForm, creditAccountId: Number(v) })}
            options={[{ value: '', label: 'Credit account…' }, ...selectAccountOptions]}
          />
          <Button onClick={() => void createJournalDraft()}>Save draft journal</Button>
        </FormGrid>
      )}

      {pending.length > 0 && (
        <div className="card p-4 mb-4 border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/30">
          <h3 className="font-semibold mb-2 text-amber-950 dark:text-amber-100">Pending approvals ({pending.length})</h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="p-2 text-left">Entity</th>
                <th className="p-2 text-left">Stage</th>
                <th className="p-2 text-left">Description</th>
                <th className="p-2" />
              </tr>
            </thead>
            <tbody>
              {pending.map((p) => (
                <tr key={`${p.entityType}-${p.entityId}-${p.stageOrder}`} className="border-b">
                  <td className="p-2">
                    {p.entityType} #{p.entityId}
                  </td>
                  <td className="p-2">
                    {p.workflowName} — {p.stageName} (stage {p.stageOrder})
                  </td>
                  <td className="p-2 text-slate-600">{p.description ?? '—'}</td>
                  <td className="p-2 text-right">
                    {canManage && (
                      <Button variant="secondary" onClick={() => void approvePending(p)}>
                        Approve
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="card overflow-x-auto">
          <h3 className="font-semibold p-3 border-b">Chart of accounts</h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="p-2 text-left">Code</th>
                <th className="p-2 text-left">Name</th>
                <th className="p-2 text-left">Type</th>
              </tr>
            </thead>
            <tbody>
              {accounts.map((a) => (
                <tr key={a.id} className="border-b">
                  <td className="p-2 font-mono">{a.accountCode}</td>
                  <td className="p-2">{a.accountName}</td>
                  <td className="p-2">{a.accountType}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <TablePagination
            page={accountsPagination.page}
            totalPages={accountsPagination.totalPages}
            totalElements={accountsPagination.totalElements}
            onPageChange={accountsPagination.setPage}
            loading={accountsLoading}
            hasRows={accounts.length > 0}
          />
        </div>
        <div className="card overflow-x-auto">
          <h3 className="font-semibold p-3 border-b">Recent journals</h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="p-2 text-left">Date</th>
                <th className="p-2 text-left">Reference</th>
                <th className="p-2 text-left">Status</th>
                <th className="p-2 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {journals.map((j) => (
                <tr key={j.id} className="border-b">
                  <td className="p-2">{j.transactionDate}</td>
                  <td className="p-2">
                    {j.referenceType}
                    {j.referenceId != null ? ` #${j.referenceId}` : ''}
                  </td>
                  <td className="p-2">{j.statusCode}</td>
                  <td className="p-2 space-x-1">
                    {canManage && j.statusCode === 'DRAFT' && (
                      <Button variant="secondary" onClick={() => void approve(j.id)}>
                        Approve
                      </Button>
                    )}
                    {canManage && j.statusCode === 'APPROVED' && (
                      <Button variant="secondary" onClick={() => void post(j.id)}>
                        Post
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <TablePagination
            page={journalsPagination.page}
            totalPages={journalsPagination.totalPages}
            totalElements={journalsPagination.totalElements}
            onPageChange={journalsPagination.setPage}
            loading={journalsLoading}
            hasRows={journals.length > 0}
          />
        </div>
      </div>
    </div>
  );
}
